import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { FormInput, EmailInput } from '../../components/ui/form-input';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Users, UserPlus, Edit2, Trash2, Key, Shield, Clock,
  Eye, EyeOff, Copy, CheckCircle, XCircle, AlertTriangle,
  History, Mail, Calendar, Settings, LayoutDashboard,
  Store, ShoppingCart, FileText, BarChart, Megaphone,
  CreditCard, HelpCircle, Database, ChevronRight, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module icon mapping
const MODULE_ICONS = {
  dashboard: LayoutDashboard,
  vendors: Store,
  finance: CreditCard,
  listings: FileText,
  orders: ShoppingCart,
  customers: Users,
  marketing: Megaphone,
  analytics: BarChart,
  communication: Mail,
  cms: Database,
  integrations: Settings,
  training: HelpCircle,
  team: Users,
  security: Shield,
  settings: Settings,
};

// Legacy ADMIN_MODULES for guest access (kept for backward compatibility)
const ADMIN_MODULES = {
  dashboard: { label: 'Dashboard', icon: LayoutDashboard, description: 'Admin dashboard overview' },
  vendors: { label: 'Vendors', icon: Store, description: 'Vendor management' },
  listings: { label: 'Listings', icon: FileText, description: 'Product listings' },
  orders: { label: 'Orders', icon: ShoppingCart, description: 'Order management' },
  customers: { label: 'Customers', icon: Users, description: 'Customer management' },
  marketing: { label: 'Marketing', icon: Megaphone, description: 'Marketing campaigns' },
  analytics: { label: 'Analytics', icon: BarChart, description: 'Reports and analytics' },
  cms: { label: 'CMS', icon: Database, description: 'Content management' },
  settings: { label: 'Settings', icon: Settings, description: 'Platform settings' },
};

// Admin roles with predefined permissions
const ADMIN_ROLES = [
  {
    id: 'super_admin',
    name: 'Super Admin',
    description: 'Full access to all modules and settings',
    permissions: Object.keys(ADMIN_MODULES),
    isDefault: false,
    canDelete: false
  },
  {
    id: 'admin',
    name: 'Admin',
    description: 'Access to most modules except critical settings',
    permissions: ['dashboard', 'vendors', 'listings', 'orders', 'customers', 'marketing', 'analytics', 'cms', 'support'],
    isDefault: true,
    canDelete: false
  },
  {
    id: 'financial_auditor',
    name: 'Financial Auditor',
    description: 'Access to financial reports and transactions',
    permissions: ['dashboard', 'analytics', 'finance'],
    isDefault: false,
    canDelete: true
  },
  {
    id: 'content_moderator',
    name: 'Content Moderator',
    description: 'Review and approve listings, manage CMS',
    permissions: ['dashboard', 'listings', 'cms'],
    isDefault: false,
    canDelete: true
  },
  {
    id: 'vendor_support',
    name: 'Vendor Support',
    description: 'Handle vendor queries and compliance',
    permissions: ['dashboard', 'vendors', 'support'],
    isDefault: false,
    canDelete: true
  },
  {
    id: 'marketing_manager',
    name: 'Marketing Manager',
    description: 'Manage campaigns and promotions',
    permissions: ['dashboard', 'marketing', 'analytics'],
    isDefault: false,
    canDelete: true
  },
  {
    id: 'customer_support',
    name: 'Customer Support',
    description: 'Handle customer queries and orders',
    permissions: ['dashboard', 'customers', 'orders', 'support'],
    isDefault: false,
    canDelete: true
  }
];

// Recursive Permission Tree Component (moved outside for React optimization)
const PermissionTreeNode = ({ 
  moduleKey, 
  moduleData, 
  depth = 0,
  expandedModules,
  toggleModuleExpanded,
  roleForm,
  togglePermission,
  isSuperAdmin
}) => {
  const Icon = MODULE_ICONS[moduleKey] || Settings;
  const isExpanded = expandedModules[moduleKey];
  const hasSubModules = moduleData.sub_modules && Object.keys(moduleData.sub_modules).length > 0;
  const hasPermissions = moduleData.permissions && moduleData.permissions.length > 0;
  const isSuperAdminOnlyModule = moduleData.super_admin_only === true;
  
  // Check if a permission is super_admin_only
  const isSuperAdminOnlyPermission = (permission) => {
    if (moduleData.super_admin_only === true) return true;
    if (Array.isArray(moduleData.super_admin_only)) {
      return moduleData.super_admin_only.includes(permission);
    }
    return false;
  };
  
  // Check if current user can grant a permission
  const canGrantPermission = (permission) => {
    if (!isSuperAdminOnlyPermission(permission)) return true;
    return isSuperAdmin;
  };
  
  return (
    <div className={`${depth > 0 ? 'ml-6 border-l-2 border-gray-100' : ''}`}>
      {/* Module header */}
      <div 
        className={`flex items-center gap-2 p-3 ${
          depth === 0 ? 'bg-gray-50 border-b' : 'hover:bg-gray-50'
        } ${isSuperAdminOnlyModule && !isSuperAdmin ? 'opacity-50' : ''}`}
      >
        {/* Expand/collapse button */}
        {hasSubModules ? (
          <button
            type="button"
            onClick={() => toggleModuleExpanded(moduleKey)}
            className="p-1 hover:bg-gray-200 rounded"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        ) : (
          <div className="w-6" />
        )}
        
        {/* Icon */}
        {depth === 0 && <Icon className="h-5 w-5 text-gray-500" />}
        
        {/* Label and description */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`${depth === 0 ? 'font-semibold' : 'font-medium'} text-sm`}>
              {moduleData.label}
            </span>
            {isSuperAdminOnlyModule && (
              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                <Shield className="h-3 w-3 mr-1" />
                Super Admin
              </Badge>
            )}
          </div>
          {moduleData.description && (
            <p className="text-xs text-gray-500">{moduleData.description}</p>
          )}
        </div>
        
        {/* Permissions checkboxes for this level */}
        {hasPermissions && (
          <div className="flex items-center gap-2 flex-wrap">
            {moduleData.permissions.map(perm => {
              const permKey = `${moduleKey}.${perm}`;
              const isChecked = !!roleForm.permissions[permKey];
              const isSuperOnly = isSuperAdminOnlyPermission(perm);
              const canGrant = canGrantPermission(perm);
              
              return (
                <label 
                  key={perm}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded border text-xs cursor-pointer transition-all ${
                    isChecked 
                      ? 'bg-primary/10 border-primary text-primary' 
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  } ${!canGrant ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => canGrant && togglePermission(permKey)}
                    disabled={!canGrant}
                    className="h-3.5 w-3.5"
                  />
                  <span className="capitalize">{perm.replace('_', ' ')}</span>
                  {isSuperOnly && (
                    <Shield className="h-3 w-3 text-amber-500" />
                  )}
                </label>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Sub-modules (recursive) */}
      {hasSubModules && isExpanded && (
        <div className="bg-white">
          {Object.entries(moduleData.sub_modules).map(([subKey, subData]) => (
            <PermissionTreeNode
              key={subKey}
              moduleKey={`${moduleKey}.${subKey}`}
              moduleData={subData}
              depth={depth + 1}
              expandedModules={expandedModules}
              toggleModuleExpanded={toggleModuleExpanded}
              roleForm={roleForm}
              togglePermission={togglePermission}
              isSuperAdmin={isSuperAdmin}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AdminTeamManagement = () => {
  const { user, apiCall } = useAuth();
  const [activeTab, setActiveTab] = useState('members');
  const [teamMembers, setTeamMembers] = useState([]);
  const [guestAccesses, setGuestAccesses] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [roles, setRoles] = useState(ADMIN_ROLES);
  const [loading, setLoading] = useState(true);
  
  // NEW: Permission schema from backend
  const [permissionSchema, setPermissionSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);

  // Modal states
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invitationLinkDialogOpen, setInvitationLinkDialogOpen] = useState(false);
  const [invitationLink, setInvitationLink] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [generatedPasskey, setGeneratedPasskey] = useState(null);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form states
  const [memberForm, setMemberForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'admin',
    generate_new_password: false,
    send_email_notification: false,
    send_sms_notification: false,
  });

  const [guestForm, setGuestForm] = useState({
    name: '',
    email: '',
    purpose: '',
    modules: [],
    access_type: 'read',
    expires_at: '',
    max_sessions: 5,
    notes: '',
  });

  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: {},  // Changed to object: { "module.submodule.permission": true }
  });

  // State for expanded modules in permission UI
  const [expandedModules, setExpandedModules] = useState({});
  
  // Check if current user is super admin
  const isSuperAdmin = user?.role === 'super_admin' || user?.is_super_admin;

  useEffect(() => {
    fetchData();
    fetchPermissionSchema();
  }, []);
  
  // Fetch the granular permission schema from backend
  const fetchPermissionSchema = async () => {
    try {
      setSchemaLoading(true);
      const response = await apiCall('/api/admin/team/permissions-schema');
      setPermissionSchema(response.schema || response);
    } catch (error) {
      console.error('Failed to fetch permission schema:', error);
      // Fallback will use legacy ADMIN_MODULES
      setPermissionSchema(null);
    } finally {
      setSchemaLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch admin team members
      const membersRes = await apiCall('/api/admin/team/members');
      setTeamMembers(membersRes.data || membersRes || []);
      
      // Fetch guest accesses
      try {
        const guestsRes = await apiCall('/api/admin/team/guests');
        setGuestAccesses(guestsRes.data || guestsRes || []);
      } catch (e) {
        setGuestAccesses([]);
      }
      
      // Fetch audit logs
      try {
        const logsRes = await apiCall('/api/admin/team/audit?limit=20');
        setAuditLogs(logsRes.logs || logsRes.data || []);
      } catch (e) {
        setAuditLogs([]);
      }
      
      // Fetch custom roles if any
      try {
        const rolesRes = await apiCall('/api/admin/team/roles');
        const customRoles = rolesRes.data || rolesRes || [];
        setRoles([...ADMIN_ROLES, ...customRoles.filter(r => !ADMIN_ROLES.find(ar => ar.id === r.id))]);
      } catch (e) {
        setRoles(ADMIN_ROLES);
      }
      
    } catch (error) {
      console.error('Error fetching team data:', error);
      // Initialize with empty data
      setTeamMembers([]);
      setGuestAccesses([]);
      setAuditLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Team Member CRUD
  const handleSaveMember = async () => {
    setFormError(null);
    
    if (!memberForm.name || !memberForm.email) {
      setFormError('Please fill in all required fields');
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (selectedMember) {
        const response = await apiCall(`/api/admin/team/members/${selectedMember.id}`, {
          method: 'PUT',
          body: JSON.stringify(memberForm)
        });
        
        // Show temp password dialog if new password was generated
        if (response.temp_password) {
          const notificationMsg = response.notifications_sent?.length > 0 
            ? ` Notifications sent: ${response.notifications_sent.join(', ')}` 
            : '';
          toast.success(`Team member updated with new password.${notificationMsg}`);
          setInvitationLink({
            tempPassword: response.temp_password,
            email: memberForm.email,
            name: memberForm.name,
            note: response.note || 'Share this new temporary password with the team member.',
            notificationsSent: response.notifications_sent || []
          });
          setInvitationLinkDialogOpen(true);
        } else {
          toast.success('Team member updated');
        }
        setMemberModalOpen(false);
      } else {
        const response = await apiCall('/api/admin/team/members', {
          method: 'POST',
          body: JSON.stringify(memberForm)
        });
        toast.success('Team member account created');
        setMemberModalOpen(false);
        
        // Show temp password dialog
        if (response.temp_password) {
          setInvitationLink({
            tempPassword: response.temp_password,
            email: response.login_email,
            name: memberForm.name,
            note: response.login_note
          });
          setInvitationLinkDialogOpen(true);
        }
      }
      resetMemberForm();
      fetchData();
    } catch (error) {
      console.error('Error saving member:', error);
      const errorMessage = error.message || 'Failed to save team member';
      setFormError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Reset password for team member
  const handleResetPassword = async (member) => {
    try {
      const response = await apiCall(`/api/admin/team/members/${member.id}/reset-password`, {
        method: 'POST'
      });
      if (response.temp_password) {
        setInvitationLink({
          tempPassword: response.temp_password,
          email: response.login_email,
          name: response.member_name,
          note: response.note,
          message: response.message
        });
        setInvitationLinkDialogOpen(true);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.message || 'Failed to reset password');
    }
  };

  // Toggle member status (active/inactive)
  const handleToggleMemberStatus = async (member) => {
    const newStatus = member.status === 'active' ? 'inactive' : 'active';
    try {
      await apiCall(`/api/admin/team/members/${member.id}/status?status=${newStatus}`, {
        method: 'PUT'
      });
      toast.success(`Team member ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error(error.message || 'Failed to update status');
    }
  };

  // Copy credentials to clipboard
  const copyCredentials = () => {
    if (invitationLink?.tempPassword) {
      const text = `Email: ${invitationLink.email}\nTemporary Password: ${invitationLink.tempPassword}`;
      navigator.clipboard.writeText(text);
      toast.success('Credentials copied to clipboard!');
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setMemberForm({
      name: member.name,
      email: member.email,
      phone: member.phone || '',
      role: member.role,
      generate_new_password: false,
      send_email_notification: false,
      send_sms_notification: false,
    });
    setFormError(null);
    setSaving(false);
    setMemberModalOpen(true);
  };

  const resetMemberForm = () => {
    setSelectedMember(null);
    setMemberForm({ 
      name: '', 
      email: '', 
      phone: '', 
      role: 'admin',
      generate_new_password: false,
      send_email_notification: false,
      send_sms_notification: false,
    });
    setFormError(null);
    setSaving(false);
  };

  // Guest Access CRUD
  const handleSaveGuest = async () => {
    try {
      const response = await apiCall('/api/admin/team/guests', {
        method: selectedGuest ? 'PUT' : 'POST',
        body: JSON.stringify({
          ...guestForm,
          id: selectedGuest?.id
        })
      });
      
      if (response.passkey) {
        setGeneratedPasskey(response.passkey);
      }
      
      toast.success(selectedGuest ? 'Guest access updated' : 'Guest access created');
      setGuestModalOpen(false);
      resetGuestForm();
      fetchData();
    } catch (error) {
      console.error('Error saving guest:', error);
      toast.error(error.message || 'Failed to save guest access');
    }
  };

  const handleEditGuest = (guest) => {
    setSelectedGuest(guest);
    setGuestForm({
      name: guest.name,
      email: guest.email,
      purpose: guest.purpose || '',
      modules: guest.modules || [],
      access_type: guest.access_type || 'read',
      expires_at: guest.expires_at || '',
      max_sessions: guest.max_sessions || 5,
      notes: guest.notes || '',
    });
    setGuestModalOpen(true);
  };

  const resetGuestForm = () => {
    setSelectedGuest(null);
    setGuestForm({
      name: '',
      email: '',
      purpose: '',
      modules: [],
      access_type: 'read',
      expires_at: '',
      max_sessions: 5,
      notes: '',
    });
  };

  // Delete handlers
  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      if (deleteTarget.type === 'member') {
        await apiCall(`/api/admin/team/members/${deleteTarget.id}`, { method: 'DELETE' });
        toast.success('Team member removed');
      } else if (deleteTarget.type === 'guest') {
        await apiCall(`/api/admin/team/guests/${deleteTarget.id}`, { method: 'DELETE' });
        toast.success('Guest access revoked');
      } else if (deleteTarget.type === 'role') {
        await apiCall(`/api/admin/team/roles/${deleteTarget.id}`, { method: 'DELETE' });
        toast.success('Role deleted successfully');
      }
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      // Show user-friendly error message
      const errorMsg = error.message || 'Failed to delete';
      if (errorMsg.includes('reassign')) {
        toast.error(errorMsg);
      } else {
        toast.error(errorMsg);
      }
    }
  };

  // Role management
  const handleSaveRole = async () => {
    try {
      // Convert to API format - roleForm.permissions is now { "module.sub.perm": true }
      const payload = {
        name: roleForm.name,
        description: roleForm.description || '',
        permissions: roleForm.permissions,  // New format: { "module.sub.perm": true }
        id: selectedRole?.id
      };

      const endpoint = selectedRole?.id 
        ? `/api/admin/team/roles/${selectedRole.id}`
        : '/api/admin/team/roles';
      
      const method = selectedRole?.id ? 'PUT' : 'POST';

      await apiCall(endpoint, {
        method,
        body: JSON.stringify(payload)
      });
      
      toast.success(selectedRole ? 'Role updated' : 'Role created');
      setRoleModalOpen(false);
      setSelectedRole(null);
      setRoleForm({ name: '', description: '', permissions: {} });
      fetchData();
    } catch (error) {
      console.error('Role save error:', error);
      toast.error(error.message || 'Failed to save role');
    }
  };

  // Copy passkey to clipboard
  const copyPasskey = (passkey) => {
    navigator.clipboard.writeText(passkey);
    toast.success('Passkey copied to clipboard');
  };

  // Generate temporary passkey
  const generateTempPasskey = async (guestId) => {
    try {
      const response = await apiCall(`/api/admin/team/guests/${guestId}/regenerate-passkey`, {
        method: 'POST'
      });
      setGeneratedPasskey(response.passkey);
      toast.success('New passkey generated');
    } catch (error) {
      toast.error('Failed to generate passkey');
    }
  };

  // Toggle module permission in guest form
  const toggleModulePermission = (moduleId) => {
    setGuestForm(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleId)
        ? prev.modules.filter(m => m !== moduleId)
        : [...prev.modules, moduleId]
    }));
  };

  // Toggle expanded state for module in permission UI
  const toggleModuleExpanded = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  // Toggle a specific permission (e.g., "vendors.all_vendors.view")
  const togglePermission = (permissionKey) => {
    setRoleForm(prev => {
      const newPermissions = { ...prev.permissions };
      if (newPermissions[permissionKey]) {
        delete newPermissions[permissionKey];
      } else {
        newPermissions[permissionKey] = true;
      }
      return { ...prev, permissions: newPermissions };
    });
  };

  const getRoleInfo = (roleId) => {
    return roles.find(r => r.id === roleId) || { name: roleId, permissions: {} };
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 md:pb-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Team & Roles</h1>
            <p className="text-gray-500 mt-1">Manage admin team members, roles, and access permissions</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { resetMemberForm(); setMemberModalOpen(true); }}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Team Member
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="members" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Team Members</span>
              <span className="sm:hidden">Team</span>
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Roles & Permissions</span>
              <span className="sm:hidden">Roles</span>
            </TabsTrigger>
            <TabsTrigger value="guests" className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              <span className="hidden sm:inline">Guest Access</span>
              <span className="sm:hidden">Guests</span>
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              <span className="hidden sm:inline">Audit Log</span>
              <span className="sm:hidden">Audit</span>
            </TabsTrigger>
          </TabsList>

          {/* Team Members Tab */}
          <TabsContent value="members">
            <Card>
              <CardHeader>
                <CardTitle>Admin Team Members</CardTitle>
                <CardDescription>Manage your admin team and their access levels</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading...</div>
                ) : teamMembers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No team members yet</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => { resetMemberForm(); setMemberModalOpen(true); }}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add First Team Member
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Last Active</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {teamMembers.map((member) => {
                            const roleInfo = getRoleInfo(member.role);
                            return (
                              <TableRow key={member.id}>
                                <TableCell className="font-medium">{member.name}</TableCell>
                                <TableCell>{member.email}</TableCell>
                                <TableCell>
                                  <Badge variant="outline">{roleInfo.name}</Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge 
                                    variant={member.status === 'active' ? 'default' : 'secondary'}
                                    className="cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => handleToggleMemberStatus(member)}
                                    title={`Click to ${member.status === 'active' ? 'deactivate' : 'activate'}`}
                                  >
                                    {member.status || 'active'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-gray-500 text-sm">
                                  {member.last_active 
                                    ? new Date(member.last_active).toLocaleDateString() 
                                    : 'Never'}
                                </TableCell>
                                <TableCell className="text-right space-x-1">
                                  {/* Reset password button */}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-blue-600"
                                    onClick={() => handleResetPassword(member)}
                                    title="Reset password"
                                  >
                                    <Key className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditMember(member)}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600"
                                    onClick={() => {
                                      setDeleteTarget({ type: 'member', id: member.id, name: member.name });
                                      setDeleteDialogOpen(true);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-3">
                      {teamMembers.map((member) => {
                        const roleInfo = getRoleInfo(member.role);
                        return (
                          <div key={member.id} className="border rounded-lg p-4 space-y-3">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-semibold text-base">{member.name}</h4>
                                <p className="text-sm text-gray-500 break-all">{member.email}</p>
                              </div>
                              <Badge 
                                variant={member.status === 'active' ? 'default' : 'secondary'}
                                className="cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => handleToggleMemberStatus(member)}
                                title={`Click to ${member.status === 'active' ? 'deactivate' : 'activate'}`}
                              >
                                {member.status || 'active'}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Badge variant="outline">{roleInfo.name}</Badge>
                              <span className="text-xs text-gray-500">
                                Last active: {member.last_active 
                                  ? new Date(member.last_active).toLocaleDateString() 
                                  : 'Never'}
                              </span>
                            </div>
                            <div className="flex gap-2 pt-2 border-t">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 text-blue-600"
                                onClick={() => handleResetPassword(member)}
                              >
                                <Key className="h-4 w-4 mr-1" />
                                Reset Password
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditMember(member)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600"
                                onClick={() => {
                                  setDeleteTarget({ type: 'member', id: member.id, name: member.name });
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Roles & Permissions Tab */}
          <TabsContent value="roles">
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle>Roles & Permissions</CardTitle>
                  <CardDescription>Define access levels for different team roles</CardDescription>
                </div>
                <Button onClick={() => {
                  setSelectedRole(null);
                  setRoleForm({ name: '', description: '', permissions: {} });
                  setExpandedModules({});
                  setRoleModalOpen(true);
                }} className="w-full sm:w-auto">
                  <Shield className="h-4 w-4 mr-2" />
                  Create Role
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {roles.map((role) => {
                    const ModuleIcon = Shield;
                    return (
                      <div key={role.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold">{role.name}</h4>
                              {role.isDefault && (
                                <Badge variant="secondary" className="text-xs">Default</Badge>
                              )}
                              {!role.canDelete && (
                                <Badge variant="outline" className="text-xs text-gray-500">System</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{role.description}</p>
                          </div>
                          <div className="flex gap-1">
                            {role.canDelete && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedRole(role);
                                    // Seed checkbox state from the detailed granular map
                                    // ("module.sub.perm" -> "full") — this is what the
                                    // permission tree checkboxes actually key off. Only
                                    // fall back to the legacy top-level module array
                                    // (e.g. ["finance"]) for roles created before the
                                    // granular permission system existed.
                                    const hasGranular = role.granularPermissions
                                      && Object.keys(role.granularPermissions).length > 0;
                                    const perms = hasGranular
                                      ? Object.keys(role.granularPermissions).reduce((acc, k) => ({ ...acc, [k]: true }), {})
                                      : Array.isArray(role.permissions)
                                        ? role.permissions.reduce((acc, p) => ({ ...acc, [p]: true }), {})
                                        : (role.permissions || {});
                                    setRoleForm({
                                      name: role.name,
                                      description: role.description,
                                      permissions: perms
                                    });
                                    setExpandedModules({});
                                    setRoleModalOpen(true);
                                  }}
                                  title="Edit Role"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => {
                                    setDeleteTarget({ type: 'role', id: role.id, name: role.name });
                                    setDeleteDialogOpen(true);
                                  }}
                                  title="Delete Role"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {/* Handle both legacy array format and new object format */}
                          {(Array.isArray(role.permissions) 
                            ? role.permissions 
                            : Object.keys(role.permissions || {})
                          ).slice(0, 10).map((perm) => {
                            // For new format, perm is like "vendors.all_vendors.view"
                            // For legacy format, perm is like "vendors"
                            const moduleId = perm.split('.')[0];
                            const module = ADMIN_MODULES[moduleId];
                            const Icon = module?.icon || Shield;
                            const displayLabel = perm.includes('.') 
                              ? perm.split('.').map(p => p.replace(/_/g, ' ')).join(' › ')
                              : (module?.label || perm);
                            return (
                              <Badge key={perm} variant="outline" className="flex items-center gap-1 text-xs">
                                <Icon className="h-3 w-3" />
                                <span className="truncate max-w-[150px]" title={displayLabel}>
                                  {displayLabel.length > 25 ? displayLabel.slice(0, 25) + '...' : displayLabel}
                                </span>
                              </Badge>
                            );
                          })}
                          {(Array.isArray(role.permissions) 
                            ? role.permissions.length 
                            : Object.keys(role.permissions || {}).length
                          ) > 10 && (
                            <Badge variant="secondary" className="text-xs">
                              +{(Array.isArray(role.permissions) 
                                ? role.permissions.length 
                                : Object.keys(role.permissions || {}).length) - 10} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Permissions Matrix */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Permissions Matrix</CardTitle>
                <CardDescription>Overview of module access by role</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="sticky left-0 bg-white">Module</TableHead>
                        {roles.slice(0, 5).map((role) => (
                          <TableHead key={role.id} className="text-center min-w-[100px]">
                            {role.name}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(ADMIN_MODULES).map(([moduleId, module]) => {
                        const Icon = module.icon;
                        return (
                          <TableRow key={moduleId}>
                            <TableCell className="sticky left-0 bg-white font-medium">
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4 text-gray-400" />
                                {module.label}
                              </div>
                            </TableCell>
                            {roles.slice(0, 5).map((role) => {
                              // Check if role has any permission for this module
                              // Handle both legacy array format and new object format
                              const perms = role.permissions || [];
                              const hasAccess = Array.isArray(perms)
                                ? perms.includes(moduleId)
                                : Object.keys(perms).some(p => p === moduleId || p.startsWith(`${moduleId}.`));
                              
                              return (
                                <TableCell key={role.id} className="text-center">
                                  {hasAccess ? (
                                    <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                                  ) : (
                                    <XCircle className="h-5 w-5 text-gray-300 mx-auto" />
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Guest Access Tab */}
          <TabsContent value="guests">
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle>Temporary Guest Access</CardTitle>
                  <CardDescription>Grant time-limited access with temporary passkeys</CardDescription>
                </div>
                <Button onClick={() => { resetGuestForm(); setGuestModalOpen(true); }} className="w-full sm:w-auto">
                  <Key className="h-4 w-4 mr-2" />
                  Create Guest Access
                </Button>
              </CardHeader>
              <CardContent>
                {guestAccesses.length === 0 ? (
                  <div className="text-center py-8">
                    <Key className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No guest accesses created</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Create temporary access for auditors, consultants, or support staff
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {guestAccesses.map((guest) => (
                      <div key={guest.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{guest.name}</h4>
                              <Badge variant={guest.is_active ? 'default' : 'secondary'}>
                                {guest.is_active ? 'Active' : 'Expired'}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-500">{guest.email}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              Purpose: {guest.purpose || 'Not specified'}
                            </p>
                            {guest.expires_at && (
                              <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                                <Clock className="h-3 w-3" />
                                Expires: {new Date(guest.expires_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => generateTempPasskey(guest.id)}
                            >
                              <Key className="h-4 w-4 mr-1" />
                              New Key
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditGuest(guest)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={() => {
                                setDeleteTarget({ type: 'guest', id: guest.id, name: guest.name });
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-3">
                          {guest.modules?.map((moduleId) => {
                            const module = ADMIN_MODULES[moduleId];
                            return (
                              <Badge key={moduleId} variant="outline" className="text-xs">
                                {module?.label || moduleId}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <CardTitle>Audit Log</CardTitle>
                <CardDescription>Track team activity and access history</CardDescription>
              </CardHeader>
              <CardContent>
                {auditLogs.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No audit logs yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {auditLogs.map((log, index) => (
                      <div key={log.id || log.name || `${log.title || "log"}-${index}`} className="flex items-start gap-3 p-3 border rounded-lg">
                        <div className={`p-2 rounded-full ${
                          log.action === 'login' ? 'bg-green-100' :
                          log.action === 'logout' ? 'bg-gray-100' :
                          log.action === 'update' ? 'bg-blue-100' :
                          'bg-yellow-100'
                        }`}>
                          {log.action === 'login' ? <CheckCircle className="h-4 w-4 text-green-600" /> :
                           log.action === 'logout' ? <XCircle className="h-4 w-4 text-gray-600" /> :
                           log.action === 'update' ? <Edit2 className="h-4 w-4 text-blue-600" /> :
                           <AlertTriangle className="h-4 w-4 text-yellow-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{log.user_name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{log.description}</p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {log.created_at ? new Date(log.created_at).toLocaleString() : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add/Edit Team Member Modal */}
      <Dialog open={memberModalOpen} onOpenChange={setMemberModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedMember ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle>
            <DialogDescription>
              {selectedMember ? 'Update team member details and role' : 'Invite a new admin team member'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <FormInput
              id="member-name"
              label="Name"
              value={memberForm.name}
              onChange={(e) => setMemberForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Full name"
              required
              minLength={2}
              hint="Enter team member's full name"
            />
            <EmailInput
              id="member-email"
              label="Email"
              value={memberForm.email}
              onChange={(e) => setMemberForm(prev => ({ ...prev, email: e.target.value }))}
              placeholder="email@example.com"
              required
            />
            <FormInput
              id="member-phone"
              label="Phone"
              value={memberForm.phone}
              onChange={(e) => setMemberForm(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="+91 XXXXX XXXXX"
              pattern="^\+?[0-9\s\-]{10,15}$"
              patternMessage="Please enter a valid phone number"
              hint="Optional - for important notifications"
            />
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select
                value={memberForm.role}
                onValueChange={(value) => setMemberForm(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {memberForm.role && (
                <p className="text-xs text-gray-500">
                  {getRoleInfo(memberForm.role).description}
                </p>
              )}
            </div>
            
            {/* Password Generation Options - Only show when editing */}
            {selectedMember && (
              <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Generate New Password</Label>
                    <p className="text-xs text-muted-foreground">Create a new temporary password for this user</p>
                  </div>
                  <Checkbox
                    id="generate-password"
                    checked={memberForm.generate_new_password}
                    onCheckedChange={(checked) => setMemberForm(prev => ({ 
                      ...prev, 
                      generate_new_password: checked,
                      send_email_notification: checked ? prev.send_email_notification : false,
                      send_sms_notification: checked ? prev.send_sms_notification : false
                    }))}
                    data-testid="generate-password-checkbox"
                  />
                </div>
                
                {memberForm.generate_new_password && (
                  <div className="pl-4 border-l-2 border-slate-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="text-sm flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Send Email Notification
                        </Label>
                        <p className="text-xs text-muted-foreground">Email the new password to {memberForm.email}</p>
                      </div>
                      <Checkbox
                        id="send-email"
                        checked={memberForm.send_email_notification}
                        onCheckedChange={(checked) => setMemberForm(prev => ({ ...prev, send_email_notification: checked }))}
                        data-testid="send-email-checkbox"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="text-sm flex items-center gap-2">
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                          </svg>
                          Send SMS Notification
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {memberForm.phone ? `SMS to ${memberForm.phone}` : 'Add phone number to enable'}
                        </p>
                      </div>
                      <Checkbox
                        id="send-sms"
                        checked={memberForm.send_sms_notification}
                        onCheckedChange={(checked) => setMemberForm(prev => ({ ...prev, send_sms_notification: checked }))}
                        disabled={!memberForm.phone}
                        data-testid="send-sms-checkbox"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {formError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {formError}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveMember} disabled={!memberForm.name || !memberForm.email || saving}>
              {saving ? 'Saving...' : (selectedMember ? 'Update' : 'Send Invite')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Role Modal */}
      <Dialog open={roleModalOpen} onOpenChange={setRoleModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{selectedRole ? 'Edit Role' : 'Create Custom Role'}</DialogTitle>
            <DialogDescription>
              Define granular permissions for this role. Permissions marked with 🛡️ are restricted to Super Admins.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role Name *</Label>
                <Input
                  data-testid="role-name-input"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Regional Manager"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  data-testid="role-description-input"
                  value={roleForm.description}
                  onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this role"
                />
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Module Permissions</Label>
                <div className="flex items-center gap-2">
                  {!isSuperAdmin && (
                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                      <Shield className="h-3 w-3 mr-1" />
                      Some permissions require Super Admin
                    </Badge>
                  )}
                  {permissionSchema && (
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Expand all modules
                          const expanded = {};
                          Object.keys(permissionSchema).forEach(key => {
                            expanded[key] = true;
                            // Also expand sub-modules
                            const module = permissionSchema[key];
                            if (module.sub_modules) {
                              Object.keys(module.sub_modules).forEach(subKey => {
                                expanded[`${key}.${subKey}`] = true;
                              });
                            }
                          });
                          setExpandedModules(expanded);
                        }}
                        className="text-xs h-7"
                      >
                        Expand All
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setExpandedModules({})}
                        className="text-xs h-7"
                      >
                        Collapse All
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              {schemaLoading ? (
                <div className="flex items-center justify-center py-12 border rounded-lg">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Loading permission schema...</p>
                  </div>
                </div>
              ) : permissionSchema ? (
                <div className="border rounded-lg max-h-[50vh] overflow-y-auto" data-testid="permissions-container">
                  {Object.entries(permissionSchema).map(([moduleKey, moduleData]) => (
                    <PermissionTreeNode
                      key={moduleKey}
                      moduleKey={moduleKey}
                      moduleData={moduleData}
                      depth={0}
                      expandedModules={expandedModules}
                      toggleModuleExpanded={toggleModuleExpanded}
                      roleForm={roleForm}
                      togglePermission={togglePermission}
                      isSuperAdmin={isSuperAdmin}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 border rounded-lg bg-gray-50">
                  <AlertTriangle className="h-8 w-8 mx-auto text-amber-500 mb-2" />
                  <p className="text-sm text-gray-600">Failed to load permission schema.</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={fetchPermissionSchema}>
                    Retry
                  </Button>
                </div>
              )}
              
              {/* Permission Summary */}
              {roleForm.permissions && Object.keys(roleForm.permissions).length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-800 mb-2">
                    Selected Permissions ({Object.keys(roleForm.permissions).length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {Object.keys(roleForm.permissions).map((key) => (
                      <Badge key={key} variant="secondary" className="text-xs">
                        {key}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveRole} 
              disabled={!roleForm?.name || !roleForm?.permissions || Object.keys(roleForm.permissions).length === 0}
            >
              {selectedRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guest Access Modal */}
      <Dialog open={guestModalOpen} onOpenChange={setGuestModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedGuest ? 'Edit Guest Access' : 'Create Guest Access'}</DialogTitle>
            <DialogDescription>
              Grant temporary access with a time-limited passkey
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Guest Name *</Label>
                <Input
                  value={guestForm.name}
                  onChange={(e) => setGuestForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Full name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={guestForm.email}
                  onChange={(e) => setGuestForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Purpose</Label>
              <Input
                value={guestForm.purpose}
                onChange={(e) => setGuestForm(prev => ({ ...prev, purpose: e.target.value }))}
                placeholder="e.g., Financial Audit Q4 2025"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Access Type</Label>
                <Select
                  value={guestForm.access_type}
                  onValueChange={(value) => setGuestForm(prev => ({ ...prev, access_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read">Read Only</SelectItem>
                    <SelectItem value="write">Read & Write</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Expires On</Label>
                <Input
                  type="date"
                  value={guestForm.expires_at}
                  onChange={(e) => setGuestForm(prev => ({ ...prev, expires_at: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Module Access</Label>
                <span className="text-xs text-gray-500">
                  Access type: <strong>{guestForm.access_type === 'read' ? 'Read Only' : 'Read & Write'}</strong>
                </span>
              </div>
              <div className="border rounded-lg divide-y max-h-[250px] overflow-y-auto">
                {Object.entries(ADMIN_MODULES).map(([moduleId, module]) => {
                  const Icon = module.icon;
                  const isSelected = guestForm.modules.includes(moduleId);
                  const hasSubModules = module.subModules && Object.keys(module.subModules).length > 0;
                  
                  return (
                    <div key={moduleId}>
                      <label
                        className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                          isSelected ? 'bg-primary/5' : 'hover:bg-gray-50'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleModulePermission(moduleId)}
                        />
                        <Icon className="h-4 w-4 text-gray-500" />
                        <div className="flex-1">
                          <span className="text-sm font-medium">{module.label}</span>
                          {hasSubModules && (
                            <p className="text-xs text-gray-400">
                              Includes: {Object.values(module.subModules).map(s => s.label).join(', ')}
                            </p>
                          )}
                        </div>
                        {isSelected && (
                          <Badge variant="outline" className={guestForm.access_type === 'read' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}>
                            {guestForm.access_type === 'read' ? 'Read' : 'R/W'}
                          </Badge>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={guestForm.notes}
                onChange={(e) => setGuestForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes about this access..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGuestModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveGuest} 
              disabled={!guestForm.name || !guestForm.email || guestForm.modules.length === 0}
            >
              {selectedGuest ? 'Update' : 'Create & Generate Passkey'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generated Passkey Modal */}
      <Dialog open={!!generatedPasskey} onOpenChange={() => setGeneratedPasskey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temporary Access Passkey</DialogTitle>
            <DialogDescription>
              Share this passkey securely with the guest. It can only be viewed once.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-2 p-4 bg-gray-100 rounded-lg font-mono text-lg">
              <span className="flex-1">{generatedPasskey}</span>
              <Button variant="ghost" size="sm" onClick={() => copyPasskey(generatedPasskey)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-amber-600 mt-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Save this passkey now. You will not be able to see it again.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setGeneratedPasskey(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temporary Password Dialog */}
      <Dialog open={invitationLinkDialogOpen} onOpenChange={setInvitationLinkDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-blue-600" />
              Login Credentials
            </DialogTitle>
            <DialogDescription>
              Share these credentials with <strong>{invitationLink?.name}</strong> to access the platform.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {invitationLink?.message && (
              <p className="text-sm text-blue-600 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                {invitationLink.message}
              </p>
            )}
            
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Email</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    value={invitationLink?.email || ''} 
                    readOnly 
                    className="flex-1 font-mono bg-gray-50"
                  />
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(invitationLink?.email || '');
                      toast.success('Email copied!');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Temporary Password</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    value={invitationLink?.tempPassword || ''} 
                    readOnly 
                    className="flex-1 font-mono text-lg tracking-wider bg-amber-50 border-amber-200 font-bold"
                  />
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(invitationLink?.tempPassword || '');
                      toast.success('Password copied!');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{invitationLink?.note || 'The user will be required to change their password on first login.'}</span>
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setInvitationLinkDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={copyCredentials}>
              <Copy className="h-4 w-4 mr-2" />
              Copy All Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to {deleteTarget?.type === 'member' ? 'remove' : 'revoke access for'} {deleteTarget?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {deleteTarget?.type === 'member' ? 'Remove' : 'Revoke'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default AdminTeamManagement;
